import {
  formatTaskSearchGridDate,
  formatTaskSearchGridDateOnly,
  idOrDash,
  numericIdSortComparator,
} from '@AMIEWEB/GlobalSearch/Tasks/taskSearchGridDisplayUtils';
import { matchesBooleanFilter } from '@AMIEWEB/GlobalSearch/Tasks/taskSearchBooleanFilterUtils';
import { IParentTaskSearchResponse, ISubTaskSearchResponse } from 'app/models/Tasks/Tasks';

export { formatTaskSearchGridDate as formatModsLinkTaskDate };
export { formatTaskSearchGridDateOnly as formatModsLinkTaskDateOnly };
export { idOrDash, numericIdSortComparator };

export const buildModsLinkTaskGridRowFields = (task: IParentTaskSearchResponse | ISubTaskSearchResponse) => ({
  status: task.status || '—',
  from: task.senderName || '—',
  recipients: task.recipients?.map(r => `${r.firstName} ${r.lastName}`).join(', ') || '—',
  sentDate: task.sentDate || '—',
  isFollowup: task.isFollowup ? 'Yes' : 'No',
  followupDate: task.followupDate || '—',
  priority: task.priority || '—',
  subject: task.subject || '—',
  message: task.text || '—',
  isOptionApproval: task.isOptionApproval ? 'Yes' : 'No',
  category: task.category || '—',
  isFlagged: task.isFlagged == null ? '—' : task.isFlagged ? 'Yes' : 'No',
  isCoverage: task.isCoverage == null ? '—' : task.isCoverage ? 'Yes' : 'No',
  coveredFor: task.coveredEmployee?.employeeName || '—',
  facilityID: idOrDash(task.facility?.facilityId),
  facility: idOrDash(task.facility?.facilityId) === '—' ? '—' : task.facility?.facilityName || '—',
  unitID: idOrDash(task.unit?.unitId),
  unit: idOrDash(task.unit?.unitId) === '—' ? '—' : task.unit?.unitName || '—',
  orderID: idOrDash(task.orderID),
  placementID: idOrDash(task.placementID),
  candidateID: idOrDash(task.candidate?.candidateID),
  candidate: idOrDash(task.candidate?.candidateID) === '—' ? '—' : task.candidate?.candidateName || '—',
  candidateBrands: task.candidate?.brands ?? [],
  contractAffiliateID: idOrDash(task.contractAffiliateID),
  affiliateContactID: idOrDash(task.affiliateContactID),
  facilityContactID: idOrDash(task.facilityContactID),
});

export function buildModsLinkTaskSearchRows(
  items: IParentTaskSearchResponse[] | undefined,
  options?: {
    activeFlaggedFilter?: boolean;
    activeCoverageFilter?: boolean;
  },
): any[] {
  if (!items || items.length === 0) {
    return [];
  }

  const { activeFlaggedFilter, activeCoverageFilter } = options || {};
  const flatRows: any[] = [];
  const processedRootIDs = new Set<string | number>();

  items.forEach((parentTask: IParentTaskSearchResponse, parentIndex: number) => {
    const rootID = parentTask.rootTaskID ?? (parentTask as any).rootTaskId ?? `r-${parentIndex}`;
    const parentTaskId = parentTask.taskID ?? (parentTask as any).taskId ?? 0;
    const rootKey = String(rootID);
    const matchingSubTasks = (parentTask.subTasks || []).filter(
      subTask =>
        matchesBooleanFilter(subTask.isFlagged, activeFlaggedFilter) &&
        matchesBooleanFilter(subTask.isCoverage, activeCoverageFilter),
    );
    const renderRootRow =
      matchesBooleanFilter(parentTask.isFlagged, activeFlaggedFilter) &&
      matchesBooleanFilter(parentTask.isCoverage, activeCoverageFilter);

    if (renderRootRow && !processedRootIDs.has(rootKey)) {
      processedRootIDs.add(rootKey);
      flatRows.push({
        ...parentTask,
        ...buildModsLinkTaskGridRowFields(parentTask),
        id: `root-${rootKey}`,
        path: [rootKey],
        taskID: parentTaskId,
        parentTaskID: parentTaskId,
        rootTaskID: rootID,
        isRootNode: true,
      });
    }

    if (matchingSubTasks.length > 0) {
      matchingSubTasks.forEach((subTask: ISubTaskSearchResponse, subIndex: number) => {
        const subID = subTask.taskID ?? (subTask as any).taskId ?? `s-${parentIndex}-${subIndex}`;
        const subKey = String(subID);
        flatRows.push({
          ...subTask,
          ...buildModsLinkTaskGridRowFields(subTask),
          id: renderRootRow ? `task-${rootKey}-${subKey}` : `task-${subKey}`,
          path: renderRootRow ? [rootKey, subKey] : [subKey],
          taskID: subTask.taskID ?? (subTask as any).taskId ?? 0,
          parentTaskID: parentTaskId,
          rootTaskID: rootID,
        });
      });
    }
  });

  return flatRows;
}
